import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import Home from './pages/Home';
import AllBrokers from './pages/AllBrokers';
import Quiz from './pages/Quiz';
import Career from './pages/Career';
import CompareBrokers from './pages/CompareBrokers';
import BrokerDetail from './pages/BrokerDetail';
import AllCourses from './pages/AllCourses';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import AboutUs from './pages/AboutUs';
import NonRefundPolicy from './pages/NonRefundPolicy';
import EducationalDisclaimer from './pages/EducationalDisclaimer';
import ContactUs from './pages/ContactUs';
import BuildDateTime from './pages/BuildDateTime';
import AdminLayout from './pages/Admin/AdminLayout';
import AdminDashboard from './pages/Admin/Dashboard/index';
import AdminUsers from './pages/Admin/Users';
import AdminCourses from './pages/Admin/Courses';
import CourseDetail from './pages/Admin/Courses/CourseDetail';
import AdminCMSIndex from './pages/Admin/CMS/index';
import AdminCMSTerms from './pages/Admin/CMS/Terms';
import AdminCMSPrivacy from './pages/Admin/CMS/Privacy';
import AdminCMSNonRefundPolicy from './pages/Admin/CMS/NonRefundPolicy';
import AdminCMSEducationalDisclaimer from './pages/Admin/CMS/EducationalDisclaimer';
import AdminCMSAbout from './pages/Admin/CMS/About';
import AdminCMSFAQ from './pages/Admin/CMS/FAQ';
import AdminCMSBasicQuiz from './pages/Admin/CMS/BasicQuiz';
import AdminCMSContactDetails from './pages/Admin/CMS/ContactDetails';
import AdminCMSPlatformStates from './pages/Admin/CMS/PlatformStates';
import AdminCMSWhyChooseUs from './pages/Admin/CMS/WhyChooseUs';
import AdminSubscriptionPlans from './pages/Admin/SubscriptionPlans';
import AdminResumes from './pages/Admin/Resumes';
import AdminReviews from './pages/Admin/Reviews';
import AdminBrokers from './pages/Admin/Brokers';
import AdminJobOpenings from './pages/Admin/JobOpenings';
import AdminPaymentHistory from './pages/Admin/Payments/AdminPaymentHistory';
import UserCourses from './pages/User/Courses';
import UserCourseDetail from './pages/User/Courses/CourseDetail';
import UserCourseQuiz from './pages/User/Courses/CourseQuiz';
import UserBrowse from './pages/User/Browse';
import UserProfile from './pages/User/Profile';
import UserSubscription from './pages/User/Subscription';
import UserPaymentHistory from './pages/User/PaymentHistory';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import PublicCourseDetail from './pages/PublicCourseDetail';
import ResetPassword from './pages/ResetPassword';
import NoInternet from './components/NoInternet';
import PageLoader from './components/PageLoader';
import './App.css';
import './theme/dialog.css';

/** Logged-in users cannot access auth pages; admins go to admin, users stay on landing. */
function PublicOnlyRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <PageLoader />;
  if (user && isAdmin) return <Navigate to="/admin" replace />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

/** Landing at `/` is visible to guests and normal users (e.g. sidebar Home). Admins still go to admin. */
function PublicLandingRoute() {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <PageLoader />;
  if (user && isAdmin) return <Navigate to="/admin" replace />;
  return <Landing />;
}

/** User-only route rendered in normal website layout (no sidebar/dashboard shell). */
function UserOnlyRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/" replace />;
  if (isAdmin) return <Navigate to="/admin" replace />;
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-50 via-white to-primary-50">
      <Header />
      <main className="flex-1 py-8">
        <div className="max-w-7xl 4xl:max-w-10xl mx-auto px-4 sm:px-6 lg:px-8">
          {children}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  const [isOffline, setIsOffline] = React.useState(!navigator.onLine);

  React.useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // React.useEffect(() => {
  //   const handleContextMenu = (e) => {
  //     e.preventDefault();
  //   };

  //   const handleKeyDown = (e) => {
  //     // Disable F12
  //     if (e.keyCode === 123) {
  //       e.preventDefault();
  //       return false;
  //     }
  //     // Disable Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+Shift+C
  //     if (e.ctrlKey && e.shiftKey && (e.keyCode === 73 || e.keyCode === 74 || e.keyCode === 67)) {
  //       e.preventDefault();
  //       return false;
  //     }
  //     // Disable Ctrl+U (View Source)
  //     if (e.ctrlKey && e.keyCode === 85) {
  //       e.preventDefault();
  //       return false;
  //     }
  //   };

  //   document.addEventListener('contextmenu', handleContextMenu);
  //   document.addEventListener('keydown', handleKeyDown);

  //   return () => {
  //     document.removeEventListener('contextmenu', handleContextMenu);
  //     document.removeEventListener('keydown', handleKeyDown);
  //   };
  // }, []);

  if (isOffline) {
    return <NoInternet />;
  }

  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<PublicLandingRoute />} />
        <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
        <Route path="/register" element={<PublicOnlyRoute><Register /></PublicOnlyRoute>} />
        <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPassword /></PublicOnlyRoute>} />
        <Route path="/home" element={<Home />} />
        <Route path="/brokers" element={<AllBrokers />} />
        <Route path="/quiz" element={<Quiz />} />
        <Route path="/career" element={<Career />} />
        <Route path="/compare" element={<CompareBrokers />} />
        <Route path="/broker/:brokerName" element={<BrokerDetail />} />
        <Route path="/courses" element={<AllCourses />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsConditions />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/refund" element={<NonRefundPolicy />} />
        <Route path="/disclaimer" element={<EducationalDisclaimer />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/build" element={<BuildDateTime />} />
        <Route path="/reset-password" element={<PublicOnlyRoute><ResetPassword /></PublicOnlyRoute>} />
        <Route path="/courses/:courseId" element={<PublicCourseDetail />} />
        {/* Admin (login via same /login page with admin@gmail.com / Admin@123) */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="courses" element={<AdminCourses />} />
          <Route path="courses/:courseId" element={<CourseDetail />} />
          <Route path="reviews" element={<AdminReviews />} />
          <Route path="brokers" element={<AdminBrokers />} />
          <Route path="job-openings" element={<AdminJobOpenings />} />
          <Route path="resumes" element={<AdminResumes />} />
          <Route path="subscription-plans" element={<AdminSubscriptionPlans />} />
          <Route path="payments" element={<AdminPaymentHistory />} />
          <Route path="cms" element={<AdminCMSIndex />} />
          <Route path="cms/basic-quiz" element={<AdminCMSBasicQuiz />} />
          <Route path="cms/terms" element={<AdminCMSTerms />} />
          <Route path="cms/privacy" element={<AdminCMSPrivacy />} />
          <Route path="cms/non-refund" element={<AdminCMSNonRefundPolicy />} />
          <Route path="cms/educational-disclaimer" element={<AdminCMSEducationalDisclaimer />} />
          <Route path="cms/about" element={<AdminCMSAbout />} />
          <Route path="cms/faq" element={<AdminCMSFAQ />} />
          <Route path="cms/contact" element={<AdminCMSContactDetails />} />
          <Route path="cms/platform-stats" element={<AdminCMSPlatformStates />} />
          <Route path="cms/why-choose-us" element={<AdminCMSWhyChooseUs />} />
        </Route>
        {/* User pages in public-style layout (no dashboard/sidebar). */}
        <Route path="/user" element={<Navigate to="/" replace />} />
        <Route path="/user/courses" element={<UserOnlyRoute><UserCourses /></UserOnlyRoute>} />
        <Route path="/user/courses/:courseId" element={<UserOnlyRoute><UserCourseDetail /></UserOnlyRoute>} />
        <Route path="/user/courses/:courseId/quiz" element={<UserOnlyRoute><UserCourseQuiz /></UserOnlyRoute>} />
        <Route path="/user/browse" element={<UserOnlyRoute><UserBrowse /></UserOnlyRoute>} />
        <Route path="/user/browse/course/:courseId" element={<UserOnlyRoute><UserCourseDetail /></UserOnlyRoute>} />
        <Route path="/user/browse/course/:courseId/quiz" element={<UserOnlyRoute><UserCourseQuiz /></UserOnlyRoute>} />
        <Route path="/user/profile" element={<UserOnlyRoute><UserProfile /></UserOnlyRoute>} />
        <Route path="/user/subscription" element={<UserOnlyRoute><UserSubscription /></UserOnlyRoute>} />
        <Route path="/user/payment-history" element={<UserOnlyRoute><UserPaymentHistory /></UserOnlyRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
